import * as _ from 'lodash';
import * as moment from 'moment';
import MaskedInput from 'react-maskedinput';
import React, { FormEvent, useEffect, useState } from 'react'
import { Alert, Button, Col, ControlLabel, Form, FormControl, FormGroup, HelpBlock, Modal, Panel, Radio } from 'react-bootstrap'
import { IBANFFClassification, IListItem, IPostTransplantRejection, IRejectionTreatment, ITransplantRejectionDSA } from '@nkr/models/index'
import { cleanBANFF, cleanOtherDsaDetails, cleanRejection, ICleanOtherDetails, IRejection, IValidation, validationState } from './models';
import { RejectionDSAType } from '@nkr/enums/rejection-dsa-type';
import { IHlaAllele } from '@nkr/models/hla';
import BANFFClassification from './banff-classification';
import IdentifiedDSA from '../identified-dsa';

interface IProps {
    show: boolean,
    isEditMode: boolean,
    data: IPostTransplantRejection,
    transplantDate: string,
    donorTyping: IHlaAllele[],
    acuteAMRGradeList: IListItem[],
    acuteTCMRGradeList: IListItem[],
    biopsyReasonList: IListItem[],
    iftaGradeList: IListItem[],
    rejectionDSATypeList: IListItem[],
    rejectionTreatmentTypeList: IListItem[],
    graftFailureReasonList: IListItem[],
    onHide: () => void,
    onSaveRejectionEpisodeDraft: (rejection: IPostTransplantRejection) => void,
    onCreateRejectionEpisode: (rejection: IPostTransplantRejection) => void,
    onUpdateRejectionEpisode: (rejection: IPostTransplantRejection) => void,
    onDeleteRejectionEpisode: (rejectionId: number) => void,
    onUnDeleteRejectionEpisode: (rejectionId: number) => void
}

const DescriptionOfRejection = (props: IProps) => {
    let [rejectionEvent, setRejectionEvent] = useState<IRejection>({ ...cleanRejection })

    let [rejectionTreatmentTypes, setRejectionTreatmentTypes] = useState<{ id: number, otherText: string }[]>([]);
    let [rejectionTreatmentTypeOther, setRejectionTreatmentTypeOther] = useState<string>('');

    let [deNovoDsa, setDeNovoDsa] = useState<{ allele: string, mfi: number }[]>([]);
    let [persistentDsa, setPersistentDsa] = useState<{ allele: string, mfi: number }[]>([]);

    let [deNovoDsaOther, setDeNovoDsaOther] = useState<ICleanOtherDetails>({ ...cleanOtherDsaDetails });
    let [persistentDsaOther, setPersistentDsaOther] = useState<ICleanOtherDetails>({ ...cleanOtherDsaDetails });

    let [banff, setBanff] = useState<IBANFFClassification>({ ...cleanBANFF })

    let [validation, setValidation] = useState<IValidation>({ ...validationState });
    let [showValidationError, setValidationError] = useState<boolean>(false);

    useEffect(() => {
        if (!props.show) {
            cleanState();
            return;
        }

        if (props.data != null) {
            setEditableEpisode(props.data);
        }
    }, [props.show])

    const setEditableEpisode = (data: IPostTransplantRejection) => {
        let { biopsy, treatments, dsas } = data;

        setRejectionEvent(prevState => ({
            ...prevState,
            dateOfRejection: data.dateOfRejection,
            deNovoDsaPresent: data.deNovoDsaPresent,
            persistentDsaPresent: data.persistentDsaPresent,
            biopsyPerformed: data.wasBiopsyPerformed,
            ...(biopsy != null && {
                biopsyReason: biopsy.reason,
                biopsyIsAbnormal: convertToNumber(biopsy.isAbnormal),
                acuteAntibodyMediatedRejection: convertToNumber(biopsy.acuteAntibodyMediatedRejection),
                acuteAmrGrade: biopsy.acuteAmrGrade,
                borderlineRejection: convertToNumber(biopsy.borderlineRejection),
                acuteTCellMediatedRejection: convertToNumber(biopsy.acuteTCellMediatedRejection),
                acuteTcmrGrade: biopsy.acuteTcmrGrade,
                chronicActiveTCellMediatedRejection: convertToNumber(biopsy.chronicActiveTCellMediatedRejection),
                interstitialFibrosisAndTubularAtrophy: convertToNumber(biopsy.interstitialFibrosisAndTubularAtrophy),
                iftaGrade: biopsy.iftaGrade,
                c4dStainingWithoutEvidenceOfRejection: convertToNumber(biopsy.c4dStainingWithoutEvidenceOfRejection),
                otherFindings: biopsy.otherFindings
            })
        }))

        let treatmentTypes = treatments?.map(x => ({ id: x.treatmentType, otherText: x.otherDetails }))

        if (treatmentTypes != null && treatmentTypes.length > 0) {
            setRejectionTreatmentTypes(treatmentTypes);

            let other = treatmentTypes.find(x => x.id == 5);

            if (other) {
                setRejectionTreatmentTypeOther(other.otherText);
            }
        }

        let deNovo = dsas?.filter(x => x.dsaType == RejectionDSAType.DeNovo).map(x => ({ allele: x.allele, mfi: x.mfi }));
        let persistent = dsas?.filter(x => x.dsaType == RejectionDSAType.Persistent).map(x => ({ allele: x.allele, mfi: x.mfi }));

        if (deNovo != null && deNovo.length > 0) {
            setDeNovoDsa(deNovo);
        }

        if (persistent != null && persistent.length > 0) {
            setPersistentDsa(persistent);
        }

        setDeNovoDsaOther({
            nonHla: data.deNovoDsaNonHla,
            other: null,
            otherDetails: null
        })

        setPersistentDsaOther({
            nonHla: data.persistentDsaNonHla,
            other: data.persistentDsaOther,
            otherDetails: data.persistentDsaOtherDetails
        })

        if (biopsy != null && biopsy.banffClassification != null) {
            let { banffClassification } = biopsy;

            setBanff({
                id: banffClassification.id,
                biopsyId: banffClassification.biopsyId,
                tubulitis: banffClassification.tubulitis,
                intimalArteritis: banffClassification.intimalArteritis,
                c4d: banffClassification.c4d,
                interstitialFibrosis: banffClassification.interstitialFibrosis,
                tubularAtrophy: banffClassification.tubularAtrophy,
                vascularFibrousIntimalThickening: banffClassification.vascularFibrousIntimalThickening,
                mesangialMatrixExpansion: banffClassification.mesangialMatrixExpansion,
                arterioloarHyalinosis: banffClassification.arterioloarHyalinosis,
                hyalineArterioloarThickening: banffClassification.hyalineArterioloarThickening,
                totalInflammation: banffClassification.totalInflammation,
                inflammationInAreaOfIfta: banffClassification.inflammationInAreaOfIfta,
                interstitialInflammation: banffClassification.interstitialInflammation,
                glomerulitis: banffClassification.glomerulitis,
                peritubularCapillaritis: banffClassification.peritubularCapillaritis,
                gbmDoubleContours: banffClassification.gbmDoubleContours,
                tifta: banffClassification.tifta,
                pvl: banffClassification.pvl
            })
        }
    }

    const cleanState = () => {
        setDeNovoDsa([])
        setPersistentDsa([])
        setRejectionTreatmentTypes([])
        setRejectionTreatmentTypeOther('')
        setDeNovoDsaOther({ ...cleanOtherDsaDetails });
        setPersistentDsaOther({ ...cleanOtherDsaDetails });
        setRejectionEvent({ ...cleanRejection });
        setBanff({ ...cleanBANFF });
        setValidation({ ...validationState });
        setValidationError(false);
    }

    const convertToNumber = (val: boolean) => {
        if (val == null) {
            return null;
        }

        if (val) {
            return 1;
        }

        if (!val) {
            return 0;
        }
    }

    const handleSave = (saveDraft: boolean = false) => {
        try {
            let rejectionTreatments: IRejectionTreatment[] = rejectionTreatmentTypes?.map(x => ({
                id: 0,
                recipientId: 0,
                treatmentType: x.id,
                otherDetails: x.id == 5 ? rejectionTreatmentTypeOther : ''
            }));

            let deNovoDsaList: ITransplantRejectionDSA[] = deNovoDsa?.map(x => ({
                id: 0,
                rejectionId: 0,
                dsaType: RejectionDSAType.DeNovo,
                allele: x.allele,
                alleleName: x.allele,
                mfi: x.mfi
            }))

            let persistentDsaList: ITransplantRejectionDSA[] = persistentDsa?.map(x => ({
                id: 0,
                rejectionId: 0,
                dsaType: RejectionDSAType.Persistent,
                allele: x.allele,
                alleleName: x.allele,
                mfi: x.mfi
            }))

            let transplantRejectionDSA = [...deNovoDsaList, ...persistentDsaList]

            let data: IPostTransplantRejection = {
                id: props.isEditMode ? props.data.id : 0,
                isDeleted: false,
                isDraft: false,
                recipientId: props.isEditMode ? props.data.recipientId : 0,
                dateOfRejection: rejectionEvent.dateOfRejection,
                deNovoDsaPresent: checkYesNoUnknownNumber(rejectionEvent.deNovoDsaPresent),
                persistentDsaPresent: checkYesNoUnknownNumber(rejectionEvent.persistentDsaPresent),
                deNovoDsaNonHla: deNovoDsaOther.nonHla,
                persistentDsaNonHla: persistentDsaOther.nonHla,
                persistentDsaOther: persistentDsaOther.other,
                persistentDsaOtherDetails: persistentDsaOther.otherDetails,
                wasBiopsyPerformed: rejectionEvent.biopsyPerformed,
                treatments: rejectionTreatments,
                dsas: transplantRejectionDSA,
                biopsy: {
                    id: 0,
                    rejectionId: 0,
                    reason: rejectionEvent.biopsyReason,
                    isAbnormal: checkYesNoUnknownBoolean(rejectionEvent.biopsyIsAbnormal),
                    acuteAntibodyMediatedRejection: checkYesNoUnknownBoolean(rejectionEvent.acuteAntibodyMediatedRejection),
                    acuteAmrGrade: rejectionEvent.acuteAmrGrade,
                    borderlineRejection: checkYesNoUnknownBoolean(rejectionEvent.borderlineRejection),
                    acuteTCellMediatedRejection: checkYesNoUnknownBoolean(rejectionEvent.acuteTCellMediatedRejection),
                    acuteTcmrGrade: rejectionEvent.acuteTcmrGrade,
                    chronicActiveTCellMediatedRejection: checkYesNoUnknownBoolean(rejectionEvent.chronicActiveTCellMediatedRejection),
                    interstitialFibrosisAndTubularAtrophy: checkYesNoUnknownBoolean(rejectionEvent.interstitialFibrosisAndTubularAtrophy),
                    iftaGrade: rejectionEvent.iftaGrade,
                    c4dStainingWithoutEvidenceOfRejection: checkYesNoUnknownBoolean(rejectionEvent.c4dStainingWithoutEvidenceOfRejection),
                    otherFindings: rejectionEvent.otherFindings,
                    banffClassification: {
                        id: 0,
                        biopsyId: 0,
                        tubulitis: banff.tubulitis,
                        intimalArteritis: banff.intimalArteritis,
                        c4d: banff.c4d,
                        interstitialFibrosis: banff.interstitialFibrosis,
                        tubularAtrophy: banff.tubularAtrophy,
                        vascularFibrousIntimalThickening: banff.vascularFibrousIntimalThickening,
                        mesangialMatrixExpansion: banff.mesangialMatrixExpansion,
                        arterioloarHyalinosis: banff.arterioloarHyalinosis,
                        hyalineArterioloarThickening: banff.hyalineArterioloarThickening,
                        totalInflammation: banff.totalInflammation,
                        inflammationInAreaOfIfta: banff.inflammationInAreaOfIfta,
                        interstitialInflammation: banff.interstitialInflammation,
                        glomerulitis: banff.glomerulitis,
                        peritubularCapillaritis: banff.peritubularCapillaritis,
                        gbmDoubleContours: banff.gbmDoubleContours,
                        tifta: banff.tifta,
                        pvl: banff.pvl
                    }
                },
            }

            if (saveDraft) {
                cleanBodyAndHide();
                props.onSaveRejectionEpisodeDraft(data);
            } else {
                let { isValid } = validate();

                if (isValid) {
                    cleanBodyAndHide();

                    if (props.isEditMode) {
                        props.onUpdateRejectionEpisode(data);
                    } else {
                        props.onCreateRejectionEpisode(data);
                    }
                }
            }

        } catch (err) {
            console.error('handleCreateBody error:', err)
        }
    }

    const cleanBodyAndHide = () => {
        props.onHide()
    }

    const validate = () => {
        let isValid = true;
        let validationState = validation;

        let {
            dateOfRejection, deNovoDsaPresent, persistentDsaPresent, biopsyPerformed,
            biopsyReason, biopsyIsAbnormal, acuteAntibodyMediatedRejection, acuteAmrGrade,
            borderlineRejection, acuteTCellMediatedRejection, acuteTcmrGrade, chronicActiveTCellMediatedRejection,
            interstitialFibrosisAndTubularAtrophy, iftaGrade, c4dStainingWithoutEvidenceOfRejection, otherFindings
        } = rejectionEvent;

        let containsRejectionTreatmentOther = rejectionTreatmentTypes.some(x => x.id == 5);

        let deNovoDsasValid = deNovoDsaPresent != 1 || (deNovoDsa.length > 0 || deNovoDsaOther.nonHla);

        deNovoDsa.forEach(x => {
            if (!x.mfi || x.mfi == 0) {
                deNovoDsasValid = false;
            }
        })

        let persistentDsaValid = persistentDsaPresent != 1 || (persistentDsa.length > 0 || persistentDsaOther.nonHla) || (persistentDsaOther.other && persistentDsaOther.otherDetails != '');
        let persistentDsaOtherValid = persistentDsaPresent != 1 || !persistentDsaOther.other || persistentDsaOther.otherDetails != '';

        persistentDsa.forEach(x => {
            if (!x.mfi || x.mfi == 0) {
                persistentDsaValid = false;
            }
        });

        let rejectionDate = moment(dateOfRejection);

        validationState.dateOfRejectionValid = rejectionDate.isValid() && moment(props.transplantDate).isSameOrBefore(rejectionDate);
        validationState.treatmentsValid = rejectionTreatmentTypes.length > 0;
        validationState.treatmentTypeOther = containsRejectionTreatmentOther ? rejectionTreatmentTypeOther != '' : true;
        validationState.deNovoDsaPresentValid = deNovoDsaPresent != null;
        validationState.persistentDsaPresentValid = persistentDsaPresent != null;
        validationState.persistentDsaOtherValid = persistentDsaOtherValid;
        validationState.deNovoDsaValid = deNovoDsasValid;
        validationState.persistentDsaValid = persistentDsaValid;
        validationState.wasBiopsyPerformedValid = biopsyPerformed != null;
        validationState.reasonValid = biopsyPerformed != 1 || biopsyReason > -1;
        validationState.isAbnormalValid = biopsyPerformed != 1 || biopsyIsAbnormal != null;
        validationState.acuteAntibodyMediatedRejectionValid = biopsyIsAbnormal != 1 || acuteAntibodyMediatedRejection != null;
        validationState.acuteAmrGradeValid = biopsyIsAbnormal != 1 || acuteAntibodyMediatedRejection != 1 || acuteAmrGrade > -1;
        validationState.borderlineRejectionValid = biopsyIsAbnormal != 1 || borderlineRejection != null;
        validationState.acuteTCellMediatedRejectionValid = biopsyIsAbnormal != 1 || acuteTCellMediatedRejection != null;
        validationState.acuteTcmrGradeValid = biopsyIsAbnormal != 1 || acuteTCellMediatedRejection != 1 || acuteTcmrGrade > -1;
        validationState.chronicActiveTCellMediatedRejectionValid = biopsyIsAbnormal != 1 || chronicActiveTCellMediatedRejection != null;
        validationState.interstitialFibrosisAndTubularAtrophyValid = biopsyIsAbnormal != 1 || interstitialFibrosisAndTubularAtrophy != null;
        validationState.iftaGradeValid = biopsyIsAbnormal != 1 || interstitialFibrosisAndTubularAtrophy != 1 || iftaGrade > -1;
        validationState.c4dStainingWithoutEvidenceOfRejectionValid = biopsyIsAbnormal != 1 || c4dStainingWithoutEvidenceOfRejection != null;

        setValidation(prevState => ({
            ...prevState,
            ...validationState
        }));

        isValid = Object.values(validationState).every(Boolean);

        if (!isValid) {
            setValidationError(true);
        } else {
            setValidationError(false);
        }

        return { isValid };
    }

    const checkYesNoUnknownBoolean = (val: number) => {
        if (val == null) {
            return null;
        }

        if (val == 1) {
            return true;
        }

        if (val == -1) {
            return null;
        }

        return false;
    }

    const checkYesNoUnknownNumber = (val: number) => {
        if (val == -1 || val == null) {
            return -1
        }

        return val;
    }

    const handleRejectionTreatmentTypeSelect = (e: React.FormEvent<HTMLInputElement>) => {
        let id = e.currentTarget.name;

        let currentList = rejectionTreatmentTypes.filter(x => x.id == parseInt(id));

        if (currentList.length > 0) {
            let newList = rejectionTreatmentTypes.filter(x => x.id != parseInt(id));
            setRejectionTreatmentTypes(prevState => (newList));
        } else {
            currentList = rejectionTreatmentTypes;
            let treatmentObject = { id: parseInt(id), otherText: rejectionTreatmentTypeOther }
            setRejectionTreatmentTypes(prevState => ([...currentList, treatmentObject]));
        }
    }

    const handleRejectionEventChange = (e: FormEvent<FormControl & HTMLSelectElement & HTMLInputElement>) => {
        const { name, value } = e.currentTarget;

        if (!isNaN(Number(value))) {
            setRejectionEvent(prevState => ({ ...prevState, [name]: parseInt(value) }));
        } else {
            setRejectionEvent(prevState => ({ ...prevState, [name]: value }));
        }
    }

    const handleDenovoDsaOther = (nonHla: boolean, other: boolean, otherDetails: string) => {
        setDeNovoDsaOther(prevState => ({ nonHla, other, otherDetails }));
    }

    const handlePersistentDsaOther = (nonHla: boolean, other: boolean, otherDetails: string) => {
        setPersistentDsaOther(prevState => ({ nonHla, other, otherDetails }));
    }

    const handleBANFF = (e: FormEvent<FormControl & HTMLSelectElement & HTMLInputElement>) => {
        const name = e.currentTarget.name;
        const value = parseInt(e.currentTarget.value);

        setBanff(prevState => ({ ...prevState, [name]: value }))
    }

    const handleSetRejectionTreatmentTypeOther = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let { value } = e.currentTarget;
        setRejectionTreatmentTypeOther(prevState => value);
    }

    const { acuteAMRGradeList, acuteTCMRGradeList, biopsyReasonList, iftaGradeList, rejectionTreatmentTypeList } = props;

    const rejectionTreatmentTypeIds = rejectionTreatmentTypes.map(x => x.id);

    const {
        dateOfRejectionValid, deNovoDsaPresentValid, treatmentTypeOther, persistentDsaPresentValid, wasBiopsyPerformedValid,
        treatmentsValid, deNovoDsaValid, persistentDsaValid, persistentDsaOtherValid, reasonValid, isAbnormalValid, acuteAntibodyMediatedRejectionValid,
        acuteAmrGradeValid, borderlineRejectionValid, acuteTCellMediatedRejectionValid, acuteTcmrGradeValid, chronicActiveTCellMediatedRejectionValid,
        interstitialFibrosisAndTubularAtrophyValid, iftaGradeValid, c4dStainingWithoutEvidenceOfRejectionValid, otherFindingsValid,
    } = validation;

    if (props == null) {
        return <></>;
    }

    const disableFields = false;
    const formGroupMargin = '5px';

    return (
        <Modal backdrop='static' dialogClassName='rejection-episode-modal' show={props.show} onHide={cleanBodyAndHide} >
            <Modal.Header closeButton>
                {props.isEditMode ? 'Edit' : 'Add'} Rejection Episode
            </Modal.Header>
            <Modal.Body>
                {showValidationError && (
                    <Alert bsSize='small' bsStyle='danger'>
                        In order to save, please fix the fields highlighted in red
                    </Alert>
                )}
                <Form horizontal>
                    <FormGroup validationState={dateOfRejectionValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>Date of Rejection:</Col>
                        <Col xs={12} lg={7}>
                            <MaskedInput
                                name='dateOfRejection'
                                className='form-control'
                                mask='11/11/1111'
                                placeholder='mm/dd/yyyy'
                                disable={disableFields}
                                value={moment(rejectionEvent.dateOfRejection).format('MM/DD/YYYY')}
                                onChange={handleRejectionEventChange}
                            />
                            {!dateOfRejectionValid && (
                                <HelpBlock>This is required and must be ON or AFTER the transplant date</HelpBlock>
                            )}
                        </Col>
                    </FormGroup>

                    <FormGroup validationState={treatmentsValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>Type(s) of Treatment for Rejection:</Col>
                        <Col xs={12} lg={7}>
                            {rejectionTreatmentTypeList.map((reason) => (
                                <span>
                                    <div key={reason.id} style={{ display: 'inline-flex', margin: '2px 5px' }}>
                                        <span>
                                            <input
                                                type='checkbox'
                                                id={`rejection-treatment-type-${reason.id}`}
                                                name={`${reason.id}`}
                                                disabled={disableFields}
                                                checked={rejectionTreatmentTypeIds.includes(reason.id)}
                                                onChange={handleRejectionTreatmentTypeSelect}
                                                style={{ marginRight: '8px' }}
                                            />
                                        </span>
                                        <span style={{ margin: '2px' }}>
                                            <label style={{ fontWeight: 'normal', marginBottom: 0 }} htmlFor={`rejection-treatment-type-${reason.id}`}>
                                                {reason.description}
                                            </label>
                                        </span>
                                    </div>
                                </span>
                            ))}
                            {!treatmentsValid && (
                                <HelpBlock>Please select at least one treatment</HelpBlock>
                            )}
                        </Col>
                    </FormGroup>

                    {rejectionTreatmentTypeIds.includes(5) && (
                        <FormGroup style={{ margin: formGroupMargin }} validationState={treatmentTypeOther ? null : 'error'}>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Other Treatment Type:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    style={{
                                        borderColor: treatmentTypeOther ? undefined : '#a94442'
                                    }}
                                    className='form-control'
                                    name='rejectionTreatmentTypeOther'
                                    value={rejectionTreatmentTypeOther}
                                    disabled={!rejectionTreatmentTypeIds.includes(5) || disableFields}
                                    onChange={handleSetRejectionTreatmentTypeOther}
                                    maxLength={250}
                                />
                                {!treatmentTypeOther && (
                                    <HelpBlock>Please specify the type of treatment</HelpBlock>
                                )}
                            </Col>
                        </FormGroup>
                    )}

                    <FormGroup validationState={deNovoDsaPresentValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>
                            Were DeNovo (newly identified) DSA present?
                        </Col>
                        <Col xs={12} lg={7}>
                            <div style={{ minHeight: 34 }}>
                                <Radio id='deNovoDsaPresent' disabled={disableFields} name='deNovoDsaPresent' inline value={1} checked={rejectionEvent.deNovoDsaPresent == 1} onChange={handleRejectionEventChange}>Yes</Radio>
                                <Radio id='deNovoDsaPresent' disabled={disableFields} name='deNovoDsaPresent' inline value={0} checked={rejectionEvent.deNovoDsaPresent == 0} onChange={handleRejectionEventChange}>No</Radio>
                                <Radio id='deNovoDsaPresent' disabled={disableFields} name='deNovoDsaPresent' inline value={-1} checked={rejectionEvent.deNovoDsaPresent == -1} onChange={handleRejectionEventChange}>Unknown</Radio>
                            </div>
                        </Col>
                    </FormGroup>

                    {rejectionEvent.deNovoDsaPresent == 1 && (
                        <Col xs={16} lg={12}>
                            <Panel>
                                <Panel.Heading>Newly Identified DSA</Panel.Heading>
                                <Panel.Body>
                                    <IdentifiedDSA
                                        type={RejectionDSAType.DeNovo}
                                        data={deNovoDsa}
                                        otherDetails={deNovoDsaOther}
                                        dsaValid={deNovoDsaValid}
                                        persistentDsaOtherValid={true}
                                        donorTyping={props.donorTyping}
                                        disabled={disableFields}
                                        onSetDsa={setDeNovoDsa}
                                        onSetDsaOther={handleDenovoDsaOther}
                                    />
                                </Panel.Body>
                            </Panel>
                        </Col>
                    )}

                    <FormGroup validationState={persistentDsaPresentValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>
                            Were Persistent (previously identified) DSA present?
                        </Col>
                        <Col xs={12} lg={7}>
                            <div style={{ minHeight: 34 }}>
                                <Radio id='persistentDsaPresent' disabled={disableFields} name='persistentDsaPresent' inline value={1} checked={rejectionEvent.persistentDsaPresent == 1} onChange={handleRejectionEventChange}>Yes</Radio>
                                <Radio id='persistentDsaPresent' disabled={disableFields} name='persistentDsaPresent' inline value={0} checked={rejectionEvent.persistentDsaPresent == 0} onChange={handleRejectionEventChange}>No</Radio>
                                <Radio id='persistentDsaPresent' disabled={disableFields} name='persistentDsaPresent' inline value={-1} checked={rejectionEvent.persistentDsaPresent == -1} onChange={handleRejectionEventChange}>Unknown</Radio>
                            </div>
                        </Col>
                    </FormGroup>

                    {rejectionEvent.persistentDsaPresent == 1 && (
                        <Col xs={12} lg={12}>
                            <Panel>
                                <Panel.Heading>Persistent DSA</Panel.Heading>
                                <Panel.Body>
                                    <IdentifiedDSA
                                        type={RejectionDSAType.Persistent}
                                        data={persistentDsa}
                                        otherDetails={persistentDsaOther}
                                        dsaValid={persistentDsaValid}
                                        persistentDsaOtherValid={persistentDsaOtherValid}
                                        donorTyping={props.donorTyping}
                                        disabled={disableFields}
                                        onSetDsa={setPersistentDsa}
                                        onSetDsaOther={handlePersistentDsaOther}
                                    />
                                </Panel.Body>
                            </Panel>
                        </Col>
                    )}

                    <FormGroup validationState={wasBiopsyPerformedValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>
                            Was a Biopsy Performed?
                        </Col>
                        <Col xs={12} lg={7}>
                            <div style={{ minHeight: 34 }}>
                                <Radio id='biopsyPerformed' disabled={disableFields} name='biopsyPerformed' inline value={1} checked={rejectionEvent.biopsyPerformed == 1} onChange={handleRejectionEventChange}>Yes</Radio>
                                <Radio id='biopsyPerformed' disabled={disableFields} name='biopsyPerformed' inline value={0} checked={rejectionEvent.biopsyPerformed == 0} onChange={handleRejectionEventChange}>No</Radio>
                                <Radio id='biopsyPerformed' disabled={disableFields} name='biopsyPerformed' inline value={-1} checked={rejectionEvent.biopsyPerformed == -1} onChange={handleRejectionEventChange}>Unknown</Radio>
                            </div>
                        </Col>
                    </FormGroup>

                    {rejectionEvent.biopsyPerformed == 1 && (
                        <>
                            <FormGroup validationState={reasonValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>
                                    Biopsy Reason:
                                </Col>
                                <Col xs={12} lg={7}>
                                    <div style={{ minHeight: 34 }}>
                                        <FormControl
                                            componentClass='select'
                                            name='biopsyReason'
                                            style={{ width: 'initial' }}
                                            disabled={disableFields}
                                            value={rejectionEvent.biopsyReason}
                                            onChange={handleRejectionEventChange}
                                        >
                                            <option key={'biopsyReason-not-selected'} value={-1}>- Select -</option>
                                            {biopsyReasonList.map((i, index) =>
                                                <option key={'biopsyReason-' + i.id} value={i.id}>{i.description}</option>
                                            )}
                                        </FormControl>
                                    </div>
                                </Col>
                            </FormGroup>

                            <FormGroup validationState={isAbnormalValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>
                                    Biopsy Findings:
                                </Col>
                                <Col xs={12} lg={7}>
                                    <div style={{ minHeight: 34 }}>
                                        <Radio id='biopsyPerformed' disabled={disableFields} name='biopsyIsAbnormal' inline value={0} checked={rejectionEvent.biopsyIsAbnormal == 0} onChange={handleRejectionEventChange}>Normal</Radio>
                                        <Radio id='biopsyPerformed' disabled={disableFields} name='biopsyIsAbnormal' inline value={1} checked={rejectionEvent.biopsyIsAbnormal == 1} onChange={handleRejectionEventChange}>Abnormal</Radio>
                                    </div>
                                </Col>
                            </FormGroup>

                            {rejectionEvent.biopsyIsAbnormal == 1 && (
                                <>
                                    <FormGroup validationState={acuteAntibodyMediatedRejectionValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>
                                            Acute Antibody Mediated Rejection:
                                        </Col>
                                        <Col xs={12} lg={7}>
                                            <div style={{ minHeight: 34 }}>
                                                <Radio id='acuteAntibodyMediatedRejection' disabled={disableFields} name='acuteAntibodyMediatedRejection' inline value={1} checked={rejectionEvent.acuteAntibodyMediatedRejection == 1} onChange={handleRejectionEventChange}>Yes</Radio>
                                                <Radio id='acuteAntibodyMediatedRejection' disabled={disableFields} name='acuteAntibodyMediatedRejection' inline value={0} checked={rejectionEvent.acuteAntibodyMediatedRejection == 0} onChange={handleRejectionEventChange}>No</Radio>
                                            </div>
                                        </Col>
                                    </FormGroup>

                                    {rejectionEvent.acuteAntibodyMediatedRejection == 1 && (
                                        <FormGroup validationState={acuteAmrGradeValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                                            <Col xs={12} lg={5} componentClass={ControlLabel}>
                                                AMR Grade:
                                            </Col>
                                            <Col xs={12} lg={3}>
                                                <div style={{ minHeight: 34 }}>
                                                    <FormControl
                                                        componentClass='select'
                                                        name='acuteAmrGrade'
                                                        style={{ width: 'initial' }}
                                                        disabled={disableFields}
                                                        value={rejectionEvent.acuteAmrGrade}
                                                        onChange={handleRejectionEventChange}
                                                    >
                                                        <option key={'acuteAmrGrade-not-selected'} value={-1}>- Select -</option>
                                                        {acuteAMRGradeList.map((i, index) =>
                                                            <option key={'acuteAmrGrade-' + i.id} value={i.id}>{i.description}</option>
                                                        )}
                                                    </FormControl>
                                                </div>
                                            </Col>
                                        </FormGroup>
                                    )}

                                    <FormGroup validationState={borderlineRejectionValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>
                                            Borderline Rejection:
                                        </Col>
                                        <Col xs={12} lg={7}>
                                            <div style={{ minHeight: 34 }}>
                                                <Radio id='borderlineRejection' disabled={disableFields} name='borderlineRejection' inline value={1} checked={rejectionEvent.borderlineRejection == 1} onChange={handleRejectionEventChange}>Yes</Radio>
                                                <Radio id='borderlineRejection' disabled={disableFields} name='borderlineRejection' inline value={0} checked={rejectionEvent.borderlineRejection == 0} onChange={handleRejectionEventChange}>No</Radio>
                                            </div>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup validationState={acuteTCellMediatedRejectionValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>
                                            Acute T Cell Mediated Rejection :
                                        </Col>
                                        <Col xs={12} lg={7}>
                                            <div style={{ minHeight: 34 }}>
                                                <Radio id='acuteTCellMediatedRejection' disabled={disableFields} name='acuteTCellMediatedRejection' inline value={1} checked={rejectionEvent.acuteTCellMediatedRejection == 1} onChange={handleRejectionEventChange}>Yes</Radio>
                                                <Radio id='acuteTCellMediatedRejection' disabled={disableFields} name='acuteTCellMediatedRejection' inline value={0} checked={rejectionEvent.acuteTCellMediatedRejection == 0} onChange={handleRejectionEventChange}>No</Radio>
                                            </div>
                                        </Col>
                                    </FormGroup>

                                    {rejectionEvent.acuteTCellMediatedRejection == 1 && (
                                        <FormGroup validationState={acuteTcmrGradeValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                                            <Col xs={12} lg={5} componentClass={ControlLabel}>
                                                ATCMR  Grade:
                                            </Col>
                                            <Col xs={12} lg={3}>
                                                <div style={{ minHeight: 34 }}>
                                                    <FormControl
                                                        componentClass='select'
                                                        name='acuteTcmrGrade'
                                                        style={{ width: 'initial' }}
                                                        disabled={disableFields}
                                                        value={rejectionEvent.acuteTcmrGrade}
                                                        onChange={handleRejectionEventChange}
                                                    >
                                                        <option key={'acuteTcmrGrade-not-selected'} value={-1}>- Select -</option>
                                                        {acuteTCMRGradeList.map((i, index) =>
                                                            <option key={'acuteTcmrGrade-' + i.id} value={i.id}>{i.description}</option>
                                                        )}
                                                    </FormControl>
                                                </div>
                                            </Col>
                                        </FormGroup>
                                    )}

                                    <FormGroup validationState={chronicActiveTCellMediatedRejectionValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>
                                            Chronic Active T Cell Mediate Rejection:
                                        </Col>
                                        <Col xs={12} lg={7}>
                                            <div style={{ minHeight: 34 }}>
                                                <Radio id='chronicActiveTCellMediatedRejection' disabled={disableFields} name='chronicActiveTCellMediatedRejection' inline value={1} checked={rejectionEvent.chronicActiveTCellMediatedRejection == 1} onChange={handleRejectionEventChange}>Yes</Radio>
                                                <Radio id='chronicActiveTCellMediatedRejection' disabled={disableFields} name='chronicActiveTCellMediatedRejection' inline value={0} checked={rejectionEvent.chronicActiveTCellMediatedRejection == 0} onChange={handleRejectionEventChange}>No</Radio>
                                            </div>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup validationState={interstitialFibrosisAndTubularAtrophyValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>
                                            Interstitial Fibrosis and Tubular Atrophy:
                                        </Col>
                                        <Col xs={12} lg={7}>
                                            <div style={{ minHeight: 34 }}>
                                                <Radio id='interstitialFibrosisAndTubularAtrophy' disabled={disableFields} name='interstitialFibrosisAndTubularAtrophy' inline value={1} checked={rejectionEvent.interstitialFibrosisAndTubularAtrophy == 1} onChange={handleRejectionEventChange}>Yes</Radio>
                                                <Radio id='interstitialFibrosisAndTubularAtrophy' disabled={disableFields} name='interstitialFibrosisAndTubularAtrophy' inline value={0} checked={rejectionEvent.interstitialFibrosisAndTubularAtrophy == 0} onChange={handleRejectionEventChange}>No</Radio>
                                            </div>
                                        </Col>
                                    </FormGroup>

                                    {rejectionEvent.interstitialFibrosisAndTubularAtrophy == 1 && (
                                        <FormGroup validationState={iftaGradeValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                                            <Col xs={12} lg={5} componentClass={ControlLabel}>
                                                IFTA Grade:
                                            </Col>
                                            <Col xs={12} lg={7}>
                                                <div style={{ minHeight: 34 }}>
                                                    <FormControl
                                                        componentClass='select'
                                                        name='iftaGrade'
                                                        style={{ width: 'initial' }}
                                                        disabled={disableFields}
                                                        value={rejectionEvent.iftaGrade}
                                                        onChange={handleRejectionEventChange}
                                                    >
                                                        <option key={'iftaGrade-not-selected'} value={-1}>- Select -</option>
                                                        {iftaGradeList.map((i, index) =>
                                                            <option key={'iftaGrade-' + i.id} value={i.id}>{i.description}</option>
                                                        )}
                                                    </FormControl>
                                                </div>
                                            </Col>
                                        </FormGroup>
                                    )}

                                    <FormGroup validationState={c4dStainingWithoutEvidenceOfRejectionValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>
                                            C4d Staining Without Evidence of Rejection:
                                        </Col>
                                        <Col xs={12} lg={7}>
                                            <div style={{ minHeight: 34 }}>
                                                <Radio id='c4dStainingWithoutEvidenceOfRejection' disabled={disableFields} name='c4dStainingWithoutEvidenceOfRejection' inline value={1} checked={rejectionEvent.c4dStainingWithoutEvidenceOfRejection == 1} onChange={handleRejectionEventChange}>Yes</Radio>
                                                <Radio id='c4dStainingWithoutEvidenceOfRejection' disabled={disableFields} name='c4dStainingWithoutEvidenceOfRejection' inline value={0} checked={rejectionEvent.c4dStainingWithoutEvidenceOfRejection == 0} onChange={handleRejectionEventChange}>No</Radio>
                                            </div>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup validationState={otherFindingsValid ? null : 'error'} style={{ margin: formGroupMargin }}>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>
                                            Other Findings:
                                        </Col>
                                        <Col xs={12} lg={7}>
                                            <FormControl
                                                name='otherFindings'
                                                componentClass='textarea'
                                                disabled={disableFields}
                                                rows={4}
                                                style={{ resize: 'none' }}
                                                value={rejectionEvent.otherFindings}
                                                onChange={handleRejectionEventChange}
                                            />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup style={{ margin: formGroupMargin }}>
                                        <Col xs={6} lg={12}>
                                            <Panel>
                                                <Panel.Heading>BANFF Classification</Panel.Heading>
                                                <Panel.Body>
                                                    <BANFFClassification
                                                        data={banff}
                                                        disabled={disableFields}
                                                        onSetBANFFClassification={handleBANFF}
                                                    />
                                                </Panel.Body>
                                            </Panel>
                                        </Col>
                                    </FormGroup>
                                </>
                            )}
                        </>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle='primary' onClick={() => handleSave(false)} disabled={disableFields}>{props.isEditMode ? 'Update' : 'Add'}</Button>
                {props.data != null && (props.data.isDraft || props.isEditMode) && (
                    <Button bsStyle='success' onClick={() => handleSave(true)} disabled={disableFields}>Save Draft</Button>
                )}
                <Button onClick={cleanBodyAndHide}>Cancel</Button>
            </Modal.Footer>
        </Modal>
    )
}

export default DescriptionOfRejection;